/**
 * Wie gross die Karte in ihrer Spalte wirklich sein darf.
 *
 * ORIGINALTEXT MARVIN (03.09.2026, mit einem Bildschirmfoto des Fensters
 * „Auftrag vorbereiten")
 *   „hier war noch rechts der text abgebrochen. wenn das noch so ist dann
 *    bitte beheben"
 *
 * ABGESCHNITTEN WAR NICHT DER TEXT, SONDERN DIE SPALTE, IN DER ER STEHT.
 * Die Kartenfläche trug eine FESTE Zahl (820 mal 520 im Lageplan, 760 mal 560
 * in der Planung). Die rechte Spalte daneben ist 340 Pixel breit und schrumpft
 * nicht (`flex: none`), und das Fenster ist auf `96vw` gedeckelt. Auf einem
 * Bildschirm mit rund 990 nutzbaren Pixeln - ein nicht ganz aufgezogenes
 * Fenster oder eine Anzeige auf 125 Prozent - ergibt das:
 *
 *     Karte 820 + Ränder ~24 + Abstand 12 + rechte Spalte 340 = 1196
 *     zur Verfügung stehen aber nur                              918
 *
 * Die Differenz läuft nach RECHTS hinaus, und dort steht die Karte mit der
 * Behördenanschrift. Aus „aufgrabungsmanagement@stadt.koblenz.de" wurde
 * „aufgrabungsmanagement@stadt.koble" - **eine Mailadresse, die man abliest
 * und die um sechs Zeichen zu kurz ist, ist schlimmer als keine.**
 *
 * DIE ZAHL WIRD DESHALB GEMESSEN UND NICHT GESETZT - dieselbe Lehre wie beim
 * Klebeband, nur eine Achse weiter. Was hier bleibt, ist das
 * SEITENVERHÄLTNIS: der Ausschnitt im Fenster soll zeigen, was auf dem
 * gedruckten Lageplan steht, und der ist A4 quer. Wer die Höhe stehenliesse
 * und nur die Breite kürzte, zeigte im Fenster einen anderen Ausschnitt als
 * im Druck - und die Markierung sässe woanders.
 */

/** Ein Kartenmass: Breite und Höhe in Bildpunkten. */
export type Kartenmass = { breite: number; hoehe: number };

/**
 * DER BODEN IST KEINE ZIER.
 *
 * Unter rund 280 Pixeln ist eine Karte kein Plan mehr, sondern ein Briefmarke:
 * die Kachelrechnung ergäbe eine einzige Kachel, und eine Markierung liesse
 * sich mit dem Daumen nicht mehr setzen. Auf dem Telefon steht die Karte
 * ohnehin allein in ihrer Spalte und bekommt deren volle Breite.
 */
export const KARTE_MINDESTBREITE = 280;

/**
 * Aus der gemessenen Breite und dem Wunschmass das wirkliche Mass rechnen.
 *
 * @param vorhanden  Was die Spalte hergibt (`clientWidth`). 0 oder kleiner
 *                   heisst „noch nicht gemessen" - dann gilt das Wunschmass,
 *                   sonst stünde beim ersten Zeichnen eine Karte von einem
 *                   Pixel Breite da.
 * @param sollBreite Das Wunschmass, und zugleich der DECKEL: eine Karte wird
 *                   nicht grösser als gedacht, nur kleiner. Ein Fenster auf
 *                   einem 4K-Schirm zöge sie sonst auf zweitausend Pixel, und
 *                   die Kachelrechnung holte viermal so viele Kacheln von
 *                   einem fremden Dienst.
 * @param sollHoehe  Die Wunschhöhe. Aus ihr und `sollBreite` kommt das
 *                   Verhältnis, das erhalten bleibt.
 */
export function kartenmass(
  vorhanden: number, sollBreite: number, sollHoehe: number,
): Kartenmass {
  const verhaeltnis = sollHoehe / sollBreite;
  if (!(vorhanden > 0)) return { breite: sollBreite, hoehe: sollHoehe };
  const breite = Math.max(KARTE_MINDESTBREITE, Math.min(sollBreite,
                                                        Math.floor(vorhanden)));
  return { breite, hoehe: Math.round(breite * verhaeltnis) };
}

/* ==========================================================================
 * DIE STUFE HAT EINE GRENZE, UND ZWAR EINE.
 *
 * ORIGINALTEXT MARVIN (03.09.2026)
 *   „«Näher/weiter» sollte ein balken sein mit einem maßstab auf beiden
 *    karten. die buttons können weg"
 *
 * Beim Ersetzen der beiden Knöpfe kam heraus, dass es die Grenze DREIMAL gab
 * und in drei verschiedenen Fassungen:
 *
 *     Knopf „näher" / „weiter"   15 bis 22   (Lageplan UND Planung)
 *     das Mausrad                14 bis 21   (`Tiefbaukarte.tsx`)
 *     der Server                 15 bis 22   (`regeln/tiefbau.ts`)
 *
 * DAS WAR NICHT NUR UNORDENTLICH, SONDERN FALSCH IN BEIDE RICHTUNGEN. Wer mit
 * dem Rad auf Stufe 14 herausging, sah einen Ausschnitt, den der Server beim
 * Merken auf 15 klemmt: das Fenster zeigte etwas anderes als das, was
 * gespeichert wurde. Und Stufe 22, die es gibt, war mit dem Rad nicht zu
 * erreichen - dieselbe Karte, zwei Bedienungen, zwei Antworten.
 *
 * DIE GRENZE DES SERVERS GEWINNT, denn sie entscheidet, was in der Datenbank
 * landet. Die Zahlen stehen hier ein zweites Mal, weil die Oberfläche kein
 * TypeScript des Servers laden kann; **Prüfstand 086 legt beide nebeneinander**
 * - dieselbe Bauart wie bei `grubenRadius` und bei den Zeitwerten aus
 * Migration 018.
 */
export const ZOOM_KLEINSTE = 15;
export const ZOOM_GROESSTE = 22;

/** Eine Stufe in die Grenzen holen. */
export function zoomKlemmen(z: number): number {
  if (!Number.isFinite(z)) return ZOOM_KLEINSTE;
  return Math.max(ZOOM_KLEINSTE, Math.min(ZOOM_GROESSTE, Math.round(z)));
}

/* ==========================================================================
 * EIN MASSSTAB IST EINE STRECKE, DIE MAN ABLESEN KANN.
 *
 * Unter der Karte stand „0,12 m je Bildpunkt". Das ist richtig gerechnet und
 * beantwortet die Frage nicht, die jemand vor einer Baustelle hat: **wie weit
 * ist das da drüben.** Wer es wissen wollte, musste eine Strecke am Bildschirm
 * schätzen und mit der Zahl multiplizieren.
 *
 * Ein Maßstabsbalken beantwortet sie ohne Rechnen: ein Balken, daneben eine
 * runde Zahl, und das Auge legt ihn an die Zeichnung an.
 *
 * DIE ZAHL IST RUND, UND DAS IST DER GANZE PUNKT. „37 m" wäre genauso wahr und
 * zum Anlegen unbrauchbar - man legt einen Balken zweimal an und rechnet dann
 * mit 74. Genommen wird die GRÖSSTE runde Zahl, deren Balken noch in den Platz
 * passt: ein Balken, der breiter ist als vorgesehen, läuft in die Karte
 * hinein.
 */
export const MASSSTAB_STUFEN = [
  1, 2, 5, 10, 20, 50, 100, 200, 500, 1_000, 2_000, 5_000,
];

export type Massstab = { meter: number; pixel: number; text: string };

/**
 * Der Maßstabsbalken zu einer Auflösung.
 *
 * @param mpp   Meter je Bildpunkt (aus `meterJePixel`).
 * @param maxPx Wie breit der Balken höchstens werden darf.
 */
export function massstab(mpp: number, maxPx = 120): Massstab {
  // Ohne brauchbare Auflösung wird nichts behauptet: ein Balken ohne Bezug ist
  // schlimmer als keiner, weil man ihn trotzdem anlegt.
  if (!Number.isFinite(mpp) || mpp <= 0 || !Number.isFinite(maxPx) || maxPx <= 0) {
    return { meter: 0, pixel: 0, text: '' };
  }
  // Die grösste Stufe, die noch passt. Passt nicht einmal die kleinste, gilt
  // sie trotzdem - dann ist der Balken schmal, und das ist die ehrliche
  // Anzeige einer sehr weit herausgezogenen Karte.
  let meter = MASSSTAB_STUFEN[0];
  for (const s of MASSSTAB_STUFEN) {
    if (s / mpp <= maxPx) meter = s; else break;
  }
  return {
    meter,
    pixel: Math.round(meter / mpp),
    text: meter >= 1_000
      ? `${String(meter / 1_000).replace('.', ',')} km`
      : `${meter} m`,
  };
}
